import { ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import get from 'lodash-es/get';

import {
    Guest,
    EmailCaptureStatus,
    EmailCaptureStyle,
    OneIdUIResult
} from '@finder/shared/services/one-id/one-id.interface';
import { CreateStyleProvider, CREATE_STYLE } from '@finder/shared/utils/dom/createStyle';
import { LottieComponent } from '../lottie/lottie.component';
import { PictureComponent } from '../picture/picture.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { OneIdUIComponent } from '../one-id-ui/one-id-ui.component';

@Component({
    selector: 'finder-email-capture',
    templateUrl: './email-capture.component.html',
    styleUrls: ['./email-capture.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        LottieComponent,
        OneIdUIComponent,
        PictureComponent,
        SafeHtmlPipe
    ]
})
export class EmailCaptureComponent implements OnInit, OnChanges {
    @Input() emailData;

    readonly EmailCaptureStatus = EmailCaptureStatus;
    guest: Guest;
    currentStatus = EmailCaptureStatus.UNSUBSCRIBED_GUEST;
    captureStyle: string | EmailCaptureStyle;
    noResult = true; // Used to hide CTA until we get a response from OneID
    containerCss: string;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(CREATE_STYLE) private createStyle: CreateStyleProvider,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.init();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.emailData.firstChange) {
            return;
        }

        this.emailData = changes.emailData.currentValue;
        this.init();
    }

    handleResult(event: OneIdUIResult) {
        this.currentStatus = event.status;
        this.emailData.hasError = event.hasError;
        this.noResult = false;

        // Force change detection
        this.changeDetectorRef.detectChanges();
    }

    private init() {
        if (!this.emailData) {
            return;
        }

        this.captureStyle = get(this.emailData, 'captureStyle', EmailCaptureStyle.MODAL); // So we can make this come from the CMS
        this.initCss();
    }

    private initCss() {
        const css = [];
        if (!this.emailData.fullWidth) {
            css.push('inner-container');
        }

        const style = this.emailData.style;
        if (style) {
            css.push(style);
        }

        if (this.emailData.hideBackground) {
            css.push('no-bg');
        } else {
            this.setBackgroundImage(style);
        }

        this.setDecorativeImage(css);

        this.containerCss = css.join(' ');
    }

    private setDecorativeImage(css: any[]) {
        const imagePlacement = get(this.emailData, 'imagePlacement', '');
        css.push(imagePlacement);
        if (imagePlacement.includes('bottom')) {
            css.push('fixed-image');

            if (imagePlacement.includes('left')) {
                css.push('fixed-left');
            } else if (imagePlacement.includes('right')) {
                css.push('fixed-right');
            }

            // Set CSS var width value to image metrics width
            const metrics = get(this.emailData, 'media1.metrics');
            const desktop = get(metrics, 'desktop', '300');
            const mobile = get(metrics, 'mobile');
            const style = `
html {
    --email-cap-mobile-picture-width: ${mobile ? mobile : desktop}px;
    --email-cap-picture-width: ${desktop}px;
}
`;
            this.createStyle(this.document, style, 'email-capture');
        }
    }

    private setBackgroundImage(style: any) {
        let bgMedia;
        if (style === 'image') {
            bgMedia = get(this.emailData, 'media.media2.mobile');
        }

        if (bgMedia) {
            const bgCss = `
html {
--ct-email-capture-bg-image: url('${bgMedia}');
--ct-email-capture-bg-image-pos: center center;
}
            `;
            this.createStyle(this.document, bgCss, 'email-capture-bg');
        }
    }
}
